import { Component } from '@theme/component';
import { buildSectionSelector, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} content
 * @property {HTMLElement} status
 * @extends {Component<Refs>}
 */
class RecentlyViewedProducts extends Component {
  requiredRefs = ['content', 'status'];

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {boolean} */
  #hasLoaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (Shopify.designMode || this.#isSectionHidden()) {
      this.#loadProducts();
      return;
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.#intersectionObserver?.disconnect();
        this.#loadProducts();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
  }

  async #loadProducts() {
    if (this.#hasLoaded) return;
    this.#hasLoaded = true;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const maxProductsDesktop = Number(this.dataset.maxProductsDesktop || 8);
    const maxProductsMobile = Number(this.dataset.maxProductsMobile || 4);
    const fetchLimit = Math.max(maxProductsDesktop, maxProductsMobile);
    const currentProductId = String(this.dataset.currentProductId || '');

    const viewedProductIds = RecentlyViewed.getProducts()
      .map((id) => String(id))
      .filter((id) => id !== '' && id !== currentProductId)
      .slice(0, fetchLimit);

    if (viewedProductIds.length === 0) {
      this.#setSectionVisibility(false);
      this.#announce('No recently viewed products.');
      return;
    }

    this.setAttribute('aria-busy', 'true');
    this.#announce('Loading recently viewed products.');

    try {
      const searchUrl = new URL(window.Theme?.routes?.search_url || '/search', window.location.origin);
      searchUrl.searchParams.set('q', viewedProductIds.map((id) => `id:${id}`).join(' OR '));
      searchUrl.searchParams.set('resources[type]', 'product');

      const sectionHtml = await sectionRenderer.getSectionHTML(sectionId, true, searchUrl);
      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const renderedSection = parsed.getElementById(buildSectionSelector(sectionId));
      const renderedContent = renderedSection?.querySelector('[data-recently-viewed-content]');

      if (!(renderedContent instanceof HTMLElement)) {
        this.#setSectionVisibility(false);
        this.#announce('No recently viewed products.');
        return;
      }

      const idOrder = new Map(viewedProductIds.map((id, index) => [id, index]));
      this.#reorderRenderedContent(renderedContent, idOrder);
      this.refs.content.replaceChildren(...Array.from(renderedContent.cloneNode(true).childNodes));

      const productIds = new Set(
        Array.from(this.refs.content.querySelectorAll('[data-product-id]'))
          .map((node) => node.getAttribute('data-product-id') || '')
          .filter((id) => id !== '')
      );

      if (productIds.size > 0) {
        this.#setSectionVisibility(true);
        this.#announce(`${productIds.size} recently viewed products loaded.`);
      } else {
        this.#setSectionVisibility(false);
        this.#announce('No recently viewed products.');
      }
    } catch (error) {
      console.error('Recently viewed section error:', error);
      this.#setSectionVisibility(false);
      this.#announce('Unable to load recently viewed products.');
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #isSectionHidden() {
    return this.#getSectionElement()?.hidden === true;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSectionElement() {
    const section = this.closest('.shopify-section');
    return section instanceof HTMLElement ? section : null;
  }

  /**
   * @param {boolean} visible
   */
  #setSectionVisibility(visible) {
    const section = this.#getSectionElement();
    if (section) {
      section.hidden = !visible;
    }
    this.hidden = !visible;
  }

  /**
   * Ensure cards follow localStorage order (newest first).
   * @param {HTMLElement} renderedContent
   * @param {Map<string, number>} idOrder
   */
  #reorderRenderedContent(renderedContent, idOrder) {
    this.#reorderListByProductId(renderedContent.querySelector('[data-recently-viewed-list]'), idOrder);
    this.#reorderListByProductId(renderedContent.querySelector('[data-recently-viewed-mobile-list]'), idOrder);
    this.#reorderCarouselByProductId(renderedContent.querySelector('[data-recently-viewed-mobile-carousel]'), idOrder);
  }

  /**
   * @param {Element | null} listElement
   * @param {Map<string, number>} idOrder
   */
  #reorderListByProductId(listElement, idOrder) {
    if (!(listElement instanceof HTMLElement)) return;

    const items = Array.from(listElement.children).filter((node) => node instanceof HTMLElement);
    if (items.length < 2) return;

    items.sort((a, b) => this.#compareByProductId(a, b, idOrder));
    listElement.replaceChildren(...items);
  }

  /**
   * @param {Element | null} carouselElement
   * @param {Map<string, number>} idOrder
   */
  #reorderCarouselByProductId(carouselElement, idOrder) {
    if (!(carouselElement instanceof HTMLElement)) return;

    const slidesContainer = carouselElement.querySelector('slideshow-slides');
    if (!(slidesContainer instanceof HTMLElement)) return;

    const slides = Array.from(slidesContainer.children).filter((node) => node instanceof HTMLElement);
    if (slides.length < 2) return;

    slides.sort((a, b) => {
      const aProduct = a.querySelector('[data-product-id]');
      const bProduct = b.querySelector('[data-product-id]');
      return this.#compareIds(
        aProduct?.getAttribute('data-product-id') || '',
        bProduct?.getAttribute('data-product-id') || '',
        idOrder
      );
    });

    slidesContainer.replaceChildren(...slides);
  }

  /**
   * @param {HTMLElement} a
   * @param {HTMLElement} b
   * @param {Map<string, number>} idOrder
   */
  #compareByProductId(a, b, idOrder) {
    return this.#compareIds(a.dataset.productId || '', b.dataset.productId || '', idOrder);
  }

  /**
   * @param {string} idA
   * @param {string} idB
   * @param {Map<string, number>} idOrder
   */
  #compareIds(idA, idB, idOrder) {
    const aOrder = idOrder.get(idA) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = idOrder.get(idB) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder;
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    this.refs.status.textContent = message;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
